/**
 * DeadPackResolutionPanel — surfaces any expired (DEAD) member packs for
 * a parent and offers a 3-radio resolution modal so the coach can pick:
 *
 *   1. Revive into a new pack ($2.50/sess preserve fee + new pack charge)
 *   2. Preserve standalone ($5/sess, evergreen while membership active)
 *   3. 2:1 courtesy ratio (default if parent does nothing, no charge)
 *
 * Coach types a reason (required) and confirms via a pop-up showing the
 * exact dollar math before the charge fires.
 *
 * Renders nothing when there are no dead packs — clean UI.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Loader2, X, DollarSign, Gift } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) => `$${(cents / 100).toFixed(2)}`;

// 7-pack catalog (kept in sync with backend PACK_CATALOG)
const PACK_OPTIONS = [
    { key: "member_auto_9", label: "Member · Auto · 9-session" },
    { key: "member_auto_12", label: "Member · Auto · 12-session" },
    { key: "member_auto_18", label: "Member · Auto · 18-session" },
    { key: "member_manual_9", label: "Member · Manual · 9-session" },
    { key: "member_manual_12", label: "Member · Manual · 12-session" },
    { key: "member_manual_18", label: "Member · Manual · 18-session" },
    { key: "nonmember_single", label: "Non-Member · Single session" },
    { key: "nonmember_5", label: "Non-Member · 5-session" },
    { key: "nonmember_10", label: "Non-Member · 10-session" },
];

export default function DeadPackResolutionPanel({ userId }) {
    const [packs, setPacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);  // pack being resolved

    const load = useCallback(async () => {
        if (!userId) return;
        try {
            const r = await axios.get(`${API}/coach/clients/${userId}/dead-packs`, { withCredentials: true });
            setPacks(r.data.dead_packs || []);
        } catch {
            setPacks([]);
        } finally {
            setLoading(false);
        }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    if (loading) return null;
    if (packs.length === 0) return null;

    return (
        <>
            <div data-testid="dead-pack-panel" className="border border-red-900/50 bg-red-950/10 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <div className="text-[11px] tracking-[0.25em] uppercase text-red-300 font-semibold">
                        Dead packs awaiting resolution ({packs.length})
                    </div>
                </div>
                <ul className="space-y-2">
                    {packs.map((p) => (
                        <li
                            key={p.pack_id}
                            data-testid={`dead-pack-row-${p.pack_id}`}
                            className="flex items-center justify-between gap-3 bg-zinc-950/60 border border-zinc-800 rounded-lg px-4 py-3"
                        >
                            <div className="min-w-0">
                                <div className="text-sm text-white truncate">{p.label}</div>
                                <div className="text-[11px] text-zinc-400 mt-0.5">
                                    {p.remaining_sessions} unused · expired {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "(n/a)"}
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid={`dead-pack-resolve-${p.pack_id}`}
                                onClick={() => setSelected(p)}
                                className="text-xs tracking-[0.15em] uppercase px-3 py-2 border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors shrink-0"
                            >
                                Resolve
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
            {selected && (
                <ResolveModal
                    pack={selected}
                    onClose={() => setSelected(null)}
                    onResolved={() => { setSelected(null); load(); }}
                />
            )}
        </>
    );
}

function ResolveModal({ pack, onClose, onResolved }) {
    const [action, setAction] = useState("two_to_one_fallback");  // default
    const [newPackKey, setNewPackKey] = useState("member_auto_9");
    const [coachNote, setCoachNote] = useState("");
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    // Auto-fill the reason textbox with sensible default when action changes
    const remaining = pack.remaining_sessions;
    const twoToOneRevived = pack.two_to_one_revived;

    useEffect(() => {
        if (action === "revive_into_new") {
            const np = PACK_OPTIONS.find(o => o.key === newPackKey);
            setCoachNote(prev => prev || `Preserving ${remaining} dead sessions into new ${np?.label || "pack"}. Parent agreed to roll over at $2.50/sess.`);
        } else if (action === "preserve_standalone") {
            setCoachNote(prev => prev || `Parent paying $5/sess to keep ${remaining} sessions evergreen. No new pack right now.`);
        } else {
            setCoachNote(prev => prev || `No response from parent — applying 2:1 courtesy. ${twoToOneRevived} revived, ${remaining - twoToOneRevived} forfeited.`);
        }
    }, [action, newPackKey, remaining, twoToOneRevived]);

    // Fetch preview dollars whenever action or pack key changes
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const url = `${API}/coach/packs/${pack.pack_id}/resolution-preview` + (action === "revive_into_new" ? `?new_pack_key=${newPackKey}` : "");
                const r = await axios.get(url, { withCredentials: true });
                if (!cancelled) setPreview(r.data);
            } catch {
                if (!cancelled) setPreview(null);
            }
        })();
        return () => { cancelled = true; };
    }, [pack.pack_id, action, newPackKey]);

    const optionRecap = useMemo(() => {
        if (!preview) return null;
        if (action === "revive_into_new" && preview.option_revive_into_new) {
            const o = preview.option_revive_into_new;
            return {
                amount: o.total_cents,
                lines: [
                    `${remaining} preserve sessions × $2.50 = ${fmt(o.preserve_fee_cents)}`,
                    `New pack: ${o.new_pack_label} = ${fmt(o.new_pack_subtotal_cents)}`,
                    `Tax: ${fmt(o.tax_cents)}`,
                    `Combined sessions on new pack: ${o.combined_sessions}`,
                ],
            };
        }
        if (action === "preserve_standalone") {
            const o = preview.option_preserve_standalone;
            return {
                amount: o.total_cents,
                lines: [
                    `${remaining} sessions × $5.00 = ${fmt(o.preserve_fee_cents)}`,
                    `Tax: ${fmt(o.tax_cents)}`,
                    `Sessions become evergreen (no expiry while membership active)`,
                ],
            };
        }
        const o = preview.option_two_to_one_fallback;
        return {
            amount: 0,
            lines: [
                `No charge — courtesy revival`,
                `${o.revived_sessions} revived, ${o.forfeited_sessions} forfeited (2:1 ratio, ceil)`,
            ],
        };
    }, [preview, action, remaining]);

    const submit = async () => {
        if (coachNote.trim().length < 3) {
            toast.error("Note required", { description: "Add at least a quick reason for the audit log." });
            return;
        }
        setSubmitting(true);
        try {
            const body = { action, coach_note: coachNote };
            if (action === "revive_into_new") body.new_pack_key = newPackKey;
            const r = await axios.post(
                `${API}/coach/packs/${pack.pack_id}/resolve`,
                body,
                { withCredentials: true },
            );
            toast.success("Resolved", { description: r.data.message });
            onResolved?.();
        } catch (e) {
            toast.error("Resolve failed", { description: e?.response?.data?.detail });
        } finally {
            setSubmitting(false);
            setShowConfirm(false);
        }
    };

    return (
        <div
            data-testid="dead-pack-resolve-modal"
            className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
            >
                <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Resolve dead pack</div>
                        <h3 className="font-display text-2xl tracking-wider mt-0.5">{pack.label}</h3>
                        <div className="text-xs text-zinc-400 mt-1">{remaining} unused sessions</div>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="resolve-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-5 overflow-y-auto space-y-3 flex-1 min-h-0">
                    {/* Option 1 */}
                    <RadioOption
                        testid="radio-revive-into-new"
                        active={action === "revive_into_new"}
                        onClick={() => setAction("revive_into_new")}
                        icon={<DollarSign className="w-4 h-4" />}
                        title="Revive INTO a new pack — $2.50/session"
                        subtitle="Sessions roll into a brand-new pack. Parent's card is charged the preserve fee + new pack total."
                    >
                        {action === "revive_into_new" && (
                            <div className="mt-3">
                                <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">New pack</label>
                                <select
                                    data-testid="resolve-new-pack-select"
                                    value={newPackKey}
                                    onChange={(e) => setNewPackKey(e.target.value)}
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-brand-red"
                                >
                                    {PACK_OPTIONS.map((o) => (
                                        <option key={o.key} value={o.key}>{o.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </RadioOption>

                    {/* Option 2 */}
                    <RadioOption
                        testid="radio-preserve-standalone"
                        active={action === "preserve_standalone"}
                        onClick={() => setAction("preserve_standalone")}
                        icon={<DollarSign className="w-4 h-4" />}
                        title="Preserve standalone — $5/session"
                        subtitle="No new pack. Sessions become evergreen as long as their membership stays active. Parent's card is charged the preserve fee."
                    />

                    {/* Option 3 (default) */}
                    <RadioOption
                        testid="radio-two-to-one"
                        active={action === "two_to_one_fallback"}
                        onClick={() => setAction("two_to_one_fallback")}
                        icon={<Gift className="w-4 h-4" />}
                        title="2:1 courtesy ratio — no charge (default)"
                        subtitle={`${twoToOneRevived} of ${remaining} sessions revived as a courtesy. ${remaining - twoToOneRevived} forfeited. No charge.`}
                    />

                    {/* Math preview */}
                    {optionRecap && (
                        <div data-testid="resolve-preview" className="border border-zinc-800 rounded-lg p-3 bg-zinc-900/30 mt-3">
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">What will happen</div>
                            <ul className="text-[12px] text-zinc-300 space-y-1">
                                {optionRecap.lines.map((l, i) => <li key={i}>· {l}</li>)}
                            </ul>
                            {optionRecap.amount > 0 && (
                                <div className="mt-3 pt-3 border-t border-zinc-800 flex items-center justify-between">
                                    <span className="text-[11px] tracking-[0.15em] uppercase text-zinc-400">Total charge</span>
                                    <span className="text-xl font-bold text-white">{fmt(optionRecap.amount)}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Coach note (required) */}
                    <div>
                        <label className="block text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1">Reason / note (required)</label>
                        <textarea
                            data-testid="resolve-coach-note"
                            value={coachNote}
                            onChange={(e) => setCoachNote(e.target.value.slice(0, 600))}
                            rows={3}
                            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-brand-red"
                        />
                        <div className="text-[10px] text-zinc-500 text-right mt-1">{coachNote.length}/600</div>
                    </div>
                </div>

                <div className="px-6 py-4 border-t border-zinc-800 flex justify-between gap-2 shrink-0 bg-zinc-950 rounded-b-2xl">
                    <button type="button" onClick={onClose} disabled={submitting} className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white">
                        Cancel
                    </button>
                    <button
                        type="button"
                        data-testid="resolve-submit"
                        onClick={() => setShowConfirm(true)}
                        disabled={submitting || coachNote.trim().length < 3}
                        className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 bg-brand-red border border-brand-red text-white hover:bg-brand-red/90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    >
                        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        Continue
                    </button>
                </div>
            </div>

            {/* Confirmation pop-up */}
            {showConfirm && optionRecap && (
                <div className="fixed inset-0 z-[55] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowConfirm(false)}>
                    <div
                        data-testid="resolve-confirm"
                        onClick={(e) => e.stopPropagation()}
                        className="bg-zinc-950 border border-brand-red rounded-2xl p-6 max-w-md w-full shadow-2xl"
                    >
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-2">Confirm</div>
                        <h3 className="font-display text-2xl tracking-wider mb-4">{
                            optionRecap.amount > 0
                                ? `Charge ${fmt(optionRecap.amount)} now?`
                                : "Apply 2:1 courtesy revival?"
                        }</h3>
                        <ul className="text-[12px] text-zinc-300 space-y-1 mb-3">
                            {optionRecap.lines.map((l, i) => <li key={i}>· {l}</li>)}
                        </ul>
                        <p className="text-[11px] text-zinc-500 mb-4 italic">Coach note: {coachNote}</p>
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowConfirm(false)} disabled={submitting} className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 border border-zinc-700 text-zinc-300 hover:border-zinc-500 hover:text-white">
                                Back
                            </button>
                            <button
                                type="button"
                                data-testid="resolve-confirm-final"
                                onClick={submit}
                                disabled={submitting}
                                className="text-xs tracking-[0.15em] uppercase px-4 py-2.5 bg-brand-red border border-brand-red text-white hover:bg-brand-red/90 inline-flex items-center gap-1.5 disabled:opacity-50"
                            >
                                {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                {optionRecap.amount > 0 ? "Charge & Revive" : "Apply 2:1"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function RadioOption({ active, onClick, icon, title, subtitle, children, testid }) {
    return (
        <div
            data-testid={testid}
            onClick={onClick}
            className={`cursor-pointer rounded-lg border p-3 transition-colors ${
                active ? "border-brand-red bg-brand-red/5" : "border-zinc-800 hover:border-zinc-600 bg-zinc-950/40"
            }`}
        >
            <div className="flex items-start gap-2">
                <div className={`mt-0.5 w-4 h-4 rounded-full border-2 shrink-0 ${active ? "border-brand-red bg-brand-red" : "border-zinc-600"}`} />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        {icon && <span className={active ? "text-brand-red" : "text-zinc-400"}>{icon}</span>}
                        <span className="text-sm text-white font-semibold">{title}</span>
                    </div>
                    <p className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{subtitle}</p>
                    {children}
                </div>
            </div>
        </div>
    );
}
